// What-if simulation engine for equity scenarios
import { supabase } from './supabase'
import {
  SimulationState,
  ScenarioParameters,
  SimulationResults,
  CapTableSnapshot,
  DilutionImpact,
  ExitPayout
} from '@/types/simulation'
import { EquityCalculationEngine } from './equity-calculator-v2'
// Generate UUID using crypto.randomUUID()
const generateUUID = () => crypto.randomUUID()

export class SimulationEngine {
  private projectId: string
  private simulationId: string

  constructor(projectId: string) {
    this.projectId = projectId
    this.simulationId = generateUUID()
  }

  async runSimulation(
    scenarioType: SimulationState['scenario_type'],
    parameters: ScenarioParameters
  ): Promise<SimulationResults> {
    // Capture current state
    const capTableBefore = await this.captureCurrentCapTable()

    // Run scenario-specific simulation
    let results: SimulationResults

    switch (scenarioType) {
      case 'new_hire':
        results = await this.simulateNewHire(parameters, capTableBefore)
        break
      case 'funding_round':
        results = await this.simulateFundingRound(parameters, capTableBefore)
        break
      case 'exit':
        results = await this.simulateExit(parameters, capTableBefore)
        break
      case 'milestone':
        results = await this.simulateMilestone(parameters, capTableBefore)
        break
      case 'option_exercise':
        results = await this.simulateOptionExercise(parameters, capTableBefore)
        break
      default:
        throw new Error(`Unknown scenario type: ${scenarioType}`)
    }

    return results
  }

  private async captureCurrentCapTable(): Promise<CapTableSnapshot> {
    // Fetch current cap table data
    const [capTableEntries, equityClasses, shareStats] = await Promise.all([
      this.fetchCapTableEntries(),
      this.fetchEquityClasses(),
      this.fetchShareStatistics()
    ])

    // Calculate current valuation
    const { data: latestRound } = await supabase
      .from('funding_rounds')
      .select('post_money_valuation')
      .eq('project_id', this.projectId)
      .order('round_date', { ascending: false })
      .limit(1)
      .single()

    const valuation = latestRound?.post_money_valuation || 0
    const totalShares = shareStats.total_shares || 10000000
    const sharePrice = totalShares > 0 ? valuation / totalShares : 0

    // Build shareholders list
    const shareholders = capTableEntries.map(entry => ({
      user_id: entry.user_id || '',
      name: entry.users ? `${entry.users.first_name || ''} ${entry.users.last_name || ''}`.trim() || entry.users.email : 'Unknown',
      shares: entry.shares || 0,
      percentage: entry.percentage || 0,
      equity_class: entry.equity_classes?.class_name || 'Common',
      vested_shares: 0
    }))

    // Summarize by equity class
    const classSummary = equityClasses.map(ec => {
      const classShares = capTableEntries
        .filter(e => e.equity_class_id === ec.id)
        .reduce((sum, e) => sum + (e.shares || 0), 0)

      return {
        class_name: ec.class_name,
        total_shares: classShares,
        percentage: totalShares > 0 ? (classShares / totalShares) * 100 : 0
      }
    })

    return {
      total_shares: totalShares,
      share_price: sharePrice,
      valuation: valuation,
      shareholders: shareholders,
      equity_classes: classSummary
    }
  }

  private async simulateNewHire(
    parameters: ScenarioParameters,
    capTableBefore: CapTableSnapshot
  ): Promise<SimulationResults> {
    if (!parameters.new_hire) {
      throw new Error('New hire parameters required')
    }

    const { new_hire } = parameters

    // Calculate new total shares
    const newTotalShares = capTableBefore.total_shares + new_hire.shares_offered

    // Create simulated new shareholder
    const newShareholder = {
      user_id: 'new_hire_' + this.simulationId,
      name: new_hire.name,
      shares: new_hire.shares_offered,
      percentage: (new_hire.shares_offered / newTotalShares) * 100,
      equity_class: await this.getEquityClassName(new_hire.equity_class_id),
      vested_shares: 0 // New hires start unvested
    }

    // Calculate dilution for existing shareholders
    const dilutionImpact: DilutionImpact[] = capTableBefore.shareholders.map(shareholder => {
      const percentageBefore = shareholder.percentage
      const percentageAfter = (shareholder.shares / newTotalShares) * 100

      return {
        user_id: shareholder.user_id,
        name: shareholder.name,
        shares_before: shareholder.shares,
        shares_after: shareholder.shares, // Shares don't change, only percentage
        percentage_before: percentageBefore,
        percentage_after: percentageAfter,
        dilution_percentage: percentageBefore - percentageAfter
      }
    })

    // Create after snapshot
    const capTableAfter: CapTableSnapshot = {
      ...capTableBefore,
      total_shares: newTotalShares,
      shareholders: [
        ...capTableBefore.shareholders.map(s => ({
          ...s,
          percentage: (s.shares / newTotalShares) * 100
        })),
        newShareholder
      ]
    }

    // Update equity class summary
    const equityClassIndex = capTableAfter.equity_classes.findIndex(
      ec => ec.class_name === newShareholder.equity_class
    )
    if (equityClassIndex >= 0) {
      capTableAfter.equity_classes[equityClassIndex].total_shares += new_hire.shares_offered
      capTableAfter.equity_classes[equityClassIndex].percentage =
        (capTableAfter.equity_classes[equityClassIndex].total_shares / newTotalShares) * 100
    }

    return {
      cap_table_before: capTableBefore,
      cap_table_after: capTableAfter,
      dilution_impact: dilutionImpact,
      warnings: this.generateWarnings('new_hire', parameters, capTableAfter)
    }
  }

  private async simulateFundingRound(
    parameters: ScenarioParameters,
    capTableBefore: CapTableSnapshot
  ): Promise<SimulationResults> {
    if (!parameters.funding_round) {
      throw new Error('Funding round parameters required')
    }

    const { funding_round } = parameters

    // Calculate price per share
    const pricePerShare = funding_round.pre_money_valuation / capTableBefore.total_shares
    const sharesIssued = Math.round(funding_round.investment_amount / pricePerShare)
    const postMoneyValuation = funding_round.pre_money_valuation + funding_round.investment_amount
    const newTotalShares = capTableBefore.total_shares + sharesIssued

    // Get equity class name once for all investors
    const equityClassName = await this.getEquityClassName(funding_round.equity_class_id)

    // Create investor entries
    const newShareholders = funding_round.investors.map(investor => {
      const investorShares = Math.round((investor.investment_amount / funding_round.investment_amount) * sharesIssued)
      return {
        user_id: `investor_${investor.name.replace(/\s+/g, '_')}_${this.simulationId}`,
        name: investor.name,
        shares: investorShares,
        percentage: (investorShares / newTotalShares) * 100,
        equity_class: equityClassName,
        vested_shares: investorShares // Investors are fully vested
      }
    })

    // Calculate dilution
    const dilutionImpact: DilutionImpact[] = capTableBefore.shareholders.map(shareholder => {
      const percentageBefore = shareholder.percentage
      const percentageAfter = (shareholder.shares / newTotalShares) * 100

      return {
        user_id: shareholder.user_id,
        name: shareholder.name,
        shares_before: shareholder.shares,
        shares_after: shareholder.shares,
        percentage_before: percentageBefore,
        percentage_after: percentageAfter,
        dilution_percentage: percentageBefore - percentageAfter
      }
    })

    // Create after snapshot
    const capTableAfter: CapTableSnapshot = {
      total_shares: newTotalShares,
      share_price: pricePerShare,
      valuation: postMoneyValuation,
      shareholders: [
        ...capTableBefore.shareholders.map(s => ({
          ...s,
          percentage: (s.shares / newTotalShares) * 100
        })),
        ...newShareholders
      ],
      equity_classes: await this.updateEquityClassSummary(
        capTableBefore.equity_classes,
        funding_round.equity_class_id,
        sharesIssued,
        newTotalShares
      )
    }

    return {
      cap_table_before: capTableBefore,
      cap_table_after: capTableAfter,
      dilution_impact: dilutionImpact,
      financial_impact: {
        cash_inflow: funding_round.investment_amount,
        new_valuation: postMoneyValuation,
        price_per_share: pricePerShare
      },
      warnings: this.generateWarnings('funding_round', parameters, capTableAfter)
    }
  }

  private async simulateExit(
    parameters: ScenarioParameters,
    capTableBefore: CapTableSnapshot
  ): Promise<SimulationResults> {
    if (!parameters.exit) {
      throw new Error('Exit parameters required')
    }

    const { exit } = parameters
    const proceeds = exit.exit_valuation - exit.expenses
    const escrowAmount = proceeds * (exit.escrow_percentage || 0) / 100
    const distributableProceeds = proceeds - escrowAmount

    // Fetch liquidation preferences
    const liquidationPrefs = await this.fetchLiquidationPreferences()

    // Calculate waterfall distribution
    const exitPayouts = await this.calculateExitWaterfall(
      capTableBefore.shareholders,
      distributableProceeds,
      liquidationPrefs
    )

    // No change to cap table in exit scenario
    const capTableAfter = capTableBefore

    return {
      cap_table_before: capTableBefore,
      cap_table_after: capTableAfter,
      dilution_impact: [], // No dilution in exit
      exit_payouts: exitPayouts,
      financial_impact: {
        cash_outflow: exit.expenses,
        new_valuation: exit.exit_valuation
      },
      warnings: this.generateWarnings('exit', parameters, capTableAfter)
    }
  }

  private async simulateMilestone(
    parameters: ScenarioParameters,
    capTableBefore: CapTableSnapshot
  ): Promise<SimulationResults> {
    if (!parameters.milestone) {
      throw new Error('Milestone parameters required')
    }

    const { milestone } = parameters
    const totalNewShares = milestone.recipients.reduce((sum, r) => sum + r.shares_granted, 0)
    const newTotalShares = capTableBefore.total_shares + totalNewShares

    // Update existing shareholders with new shares
    const updatedShareholders = capTableBefore.shareholders.map(shareholder => {
      const recipient = milestone.recipients.find(r => r.user_id === shareholder.user_id)
      const newShares = shareholder.shares + (recipient?.shares_granted || 0)

      return {
        ...shareholder,
        shares: newShares,
        percentage: (newShares / newTotalShares) * 100
      }
    })

    // Calculate dilution
    const dilutionImpact: DilutionImpact[] = capTableBefore.shareholders.map(shareholder => {
      const recipient = milestone.recipients.find(r => r.user_id === shareholder.user_id)
      const sharesAfter = shareholder.shares + (recipient?.shares_granted || 0)
      const percentageBefore = shareholder.percentage
      const percentageAfter = (sharesAfter / newTotalShares) * 100

      return {
        user_id: shareholder.user_id,
        name: shareholder.name,
        shares_before: shareholder.shares,
        shares_after: sharesAfter,
        percentage_before: percentageBefore,
        percentage_after: percentageAfter,
        dilution_percentage: percentageBefore - percentageAfter
      }
    })

    const capTableAfter: CapTableSnapshot = {
      ...capTableBefore,
      total_shares: newTotalShares,
      shareholders: updatedShareholders
    }

    return {
      cap_table_before: capTableBefore,
      cap_table_after: capTableAfter,
      dilution_impact: dilutionImpact,
      warnings: this.generateWarnings('milestone', parameters, capTableAfter)
    }
  }

  private async simulateOptionExercise(
    parameters: ScenarioParameters,
    capTableBefore: CapTableSnapshot
  ): Promise<SimulationResults> {
    if (!parameters.option_exercise) {
      throw new Error('Option exercise parameters required')
    }

    const { option_exercise } = parameters
    const exerciseCost = option_exercise.shares_to_exercise * option_exercise.exercise_price
    const currentValue = option_exercise.shares_to_exercise * option_exercise.fair_market_value
    const taxableGain = currentValue - exerciseCost

    // Find the shareholder
    const shareholderIndex = capTableBefore.shareholders.findIndex(
      s => s.user_id === option_exercise.user_id
    )

    if (shareholderIndex === -1) {
      throw new Error('Shareholder not found')
    }

    // No new shares created in exercise (converting options to shares)
    // This is simplified - in reality would need to track option vs share counts
    const capTableAfter = {
      ...capTableBefore,
      shareholders: [...capTableBefore.shareholders]
    }

    // Update vested shares for the exercising shareholder
    capTableAfter.shareholders[shareholderIndex] = {
      ...capTableAfter.shareholders[shareholderIndex],
      vested_shares: (capTableAfter.shareholders[shareholderIndex].vested_shares || 0) +
        option_exercise.shares_to_exercise
    }

    return {
      cap_table_before: capTableBefore,
      cap_table_after: capTableAfter,
      dilution_impact: [], // No dilution in option exercise
      financial_impact: {
        cash_inflow: exerciseCost,
        cash_outflow: taxableGain * 0.3 // Approximate tax withholding
      },
      warnings: this.generateWarnings('option_exercise', parameters, capTableAfter)
    }
  }

  // Helper methods
  private async fetchCapTableEntries() {
    const { data, error } = await supabase
      .from('cap_table_entries')
      .select(`
        *,
        users (id, email, first_name, last_name),
        equity_classes (id, class_name)
      `)
      .eq('project_id', this.projectId)

    if (error) throw error
    return data || []
  }

  private async fetchEquityClasses() {
    const { data, error } = await supabase
      .from('equity_classes')
      .select('*')
      .order('id')

    if (error) throw error
    return data || []
  }

  private async fetchShareStatistics() {
    // This would ideally be a database function
    const { data: offers } = await supabase
      .from('equity_offers')
      .select('shares_offered')
      .eq('project_id', this.projectId)
      .eq('status', 'accepted')

    const totalShares = offers?.reduce((sum, o) => sum + (o.shares_offered || 0), 0) || 10000000

    return { total_shares: totalShares }
  }

  private async getEquityClassName(equityClassId: string): Promise<string> {
    const { data } = await supabase
      .from('equity_classes')
      .select('class_name')
      .eq('id', equityClassId)
      .single()

    return data?.class_name || 'Common'
  }

  private async updateEquityClassSummary(
    currentClasses: CapTableSnapshot['equity_classes'],
    newClassId: string,
    newShares: number,
    totalShares: number
  ): Promise<CapTableSnapshot['equity_classes']> {
    const className = await this.getEquityClassName(newClassId)

    return currentClasses.map(ec => {
      if (ec.class_name === className) {
        const updatedShares = ec.total_shares + newShares
        return {
          ...ec,
          total_shares: updatedShares,
          percentage: (updatedShares / totalShares) * 100
        }
      }
      return {
        ...ec,
        percentage: (ec.total_shares / totalShares) * 100
      }
    })
  }

  private async fetchLiquidationPreferences() {
    const { data } = await supabase
      .from('equity_classes')
      .select('*')
      .order('liquidation_preference', { ascending: false })

    return data || []
  }

  private async calculateExitWaterfall(
    shareholders: CapTableSnapshot['shareholders'],
    proceeds: number,
    liquidationPrefs: any[]
  ): Promise<ExitPayout[]> {
    // Simplified waterfall calculation
    // In reality, this would be much more complex with participation rights, caps, etc.

    const payouts: ExitPayout[] = []
    let remainingProceeds = proceeds

    // First, pay liquidation preferences for preferred shareholders
    for (const prefClass of liquidationPrefs) {
      if (prefClass.liquidation_preference > 0) {
        const prefShareholders = shareholders.filter(
          s => s.equity_class === prefClass.class_name
        )

        for (const shareholder of prefShareholders) {
          const prefAmount = Math.min(
            shareholder.shares * prefClass.liquidation_preference,
            remainingProceeds
          )

          payouts.push({
            user_id: shareholder.user_id,
            name: shareholder.name,
            equity_class: shareholder.equity_class,
            shares: shareholder.shares,
            gross_payout: prefAmount,
            liquidation_preference_paid: prefAmount,
            net_payout: prefAmount,
            percentage_of_proceeds: (prefAmount / proceeds) * 100
          })

          remainingProceeds -= prefAmount
        }
      }
    }

    // Then distribute remaining proceeds pro-rata
    const totalCommonShares = shareholders
      .filter(s => !payouts.find(p => p.user_id === s.user_id))
      .reduce((sum, s) => sum + s.shares, 0)

    for (const shareholder of shareholders) {
      if (!payouts.find(p => p.user_id === shareholder.user_id)) {
        const proRataAmount = (shareholder.shares / totalCommonShares) * remainingProceeds

        payouts.push({
          user_id: shareholder.user_id,
          name: shareholder.name,
          equity_class: shareholder.equity_class,
          shares: shareholder.shares,
          gross_payout: proRataAmount,
          net_payout: proRataAmount,
          percentage_of_proceeds: (proRataAmount / proceeds) * 100
        })
      }
    }

    return payouts
  }

  private generateWarnings(
    scenarioType: string,
    parameters: ScenarioParameters,
    capTableAfter: CapTableSnapshot
  ): string[] {
    const warnings: string[] = []

    // Check for excessive dilution
    if (scenarioType === 'new_hire' || scenarioType === 'funding_round') {
      const maxDilution = capTableAfter.shareholders
        .map(s => {
          const before = capTableAfter.shareholders.find(sb => sb.user_id === s.user_id)
          return before ? before.percentage - s.percentage : 0
        })
        .reduce((max, d) => Math.max(max, d), 0)

      if (maxDilution > 10) {
        warnings.push(`Significant dilution detected: up to ${maxDilution.toFixed(2)}%`)
      }
    }

    // Check for option pool depletion
    const optionPoolPercentage = capTableAfter.equity_classes
      .find(ec => ec.class_name === 'Options')?.percentage || 0

    if (optionPoolPercentage < 5) {
      warnings.push('Option pool is below 5% - consider refreshing')
    }

    // Exit scenario warnings
    if (scenarioType === 'exit' && parameters.exit) {
      if (parameters.exit.exit_valuation < capTableAfter.valuation) {
        warnings.push('Exit valuation is below current valuation - down round')
      }
    }

    return warnings
  }

  // Save/Load functionality
  async saveSimulation(
    name: string,
    description: string,
    scenarioType: SimulationState['scenario_type'],
    parameters: ScenarioParameters,
    results: SimulationResults
  ): Promise<SimulationState> {
    const simulation: SimulationState = {
      id: this.simulationId,
      name,
      description,
      project_id: this.projectId,
      scenario_type: scenarioType,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      parameters,
      results,
      saved: true
    }

    // Store in database (would need a simulations table)
    const { error, data } = await supabase
      .from('simulations')
      .insert({
        project_id: simulation.project_id,
        name: simulation.name,
        description: simulation.description || null,
        scenario_type: simulation.scenario_type,
        parameters: simulation.parameters as unknown as import('@/database.types').Json,
        results: simulation.results as unknown as import('@/database.types').Json,
        created_by: simulation.created_by || '',
      })
      .select()
      .single()

    if (error) throw error

    return { ...simulation, ...data, saved: true } as unknown as SimulationState
  }

  static async loadSimulation(simulationId: string): Promise<SimulationState | null> {
    const { data, error } = await supabase
      .from('simulations')
      .select('*')
      .eq('id', simulationId)
      .single()

    if (error || !data) return null

    return {
      ...data,
      description: data.description ?? undefined,
      saved: true
    } as unknown as SimulationState
  }

  static async listSimulations(projectId: string): Promise<SimulationState[]> {
    const { data, error } = await supabase
      .from('simulations')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })

    if (error) throw error

    return (data || []).map(d => ({
      ...d,
      description: d.description ?? undefined,
      saved: true
    })) as unknown as SimulationState[]
  }
}